/* Le schede dei mercati.
 *
 * Il catalogo le manda su DUE livelli: il nodo in cima raggruppa e non
 * dichiara mercati propri (markets: []), i figli sono quelli che li
 * dichiarano davvero.
 *
 *   openTab: '1111111'
 *   schede:  ('1111111', 0 mercati, 1 figlio)
 *            ('over/under', 0 mercati, 2 figli)
 *
 * Chi legge solo il primo livello trova una lista vuota e non mostra niente.
 * E' quello che succedeva su telefono, mentre il desktop era gia' stato
 * corretto: due letture della stessa cosa, una giusta e una no.
 *
 * Da qui in avanti la lettura e' una sola.
 */

/** Le sotto-schede di una scheda, se ne ha. */
export function sottoSchede(scheda) {
  return (scheda && scheda.children) || [];
}

/** La sotto-scheda da mostrare: quella scelta, altrimenti quella
 *  predefinita, altrimenti la prima. */
export function sottoSchedaAttiva(scheda, nomeScelto) {
  const figlie = sottoSchede(scheda);
  if (!figlie.length) return null;
  return figlie.find((c) => c.name === nomeScelto)
      || figlie.find((c) => c.defaultTab)
      || figlie[0];
}

/** La scheda da mostrare: quella scelta, altrimenti quella che il servizio
 *  indica come aperta, altrimenti la prima. */
export function schedaAttiva(schede, nomeScelto, openTab) {
  const lista = schede || [];
  return lista.find((t) => t.name === (nomeScelto || openTab)) || lista[0] || null;
}

/** I mercati da mostrare per una scheda: quelli della sotto-scheda scelta
 *  quando ci sono sotto-schede, altrimenti i suoi. Mai undefined. */
export function mercatiDellaScheda(scheda, nomeSottoScelto) {
  const sotto = sottoSchedaAttiva(scheda, nomeSottoScelto);
  return (sotto ? sotto.markets : (scheda && scheda.markets)) || [];
}

/** Gli esiti di un mercato, comunque il dato li chiami.
 *
 * Tre nomi per la stessa cosa: la configurazione delle schede li manda come
 * `outcomes`, il feed dell'evento anche come `odds`, e una versione
 * precedente del catalogo li chiamava `tabOutcomes`. Il desktop gestiva gia'
 * i primi due; il telefono leggeva solo `tabOutcomes` e andava in errore
 * appena i mercati arrivavano davvero.
 *
 * Restituisce sempre un elenco, mai undefined: e' quello che evita che una
 * schermata intera sparisca per un campo con un nome diverso.
 */
export function esitiDelMercato(mercato) {
  if (!mercato) return [];
  const e = mercato.tabOutcomes || mercato.outcomes || mercato.odds;
  return Array.isArray(e) ? e : [];
}

/** Le schede nell'ordine deciso dal backoffice.
 *
 * Il catalogo manda `position`. Il desktop ordinava per `order`, che non
 * esiste: parseFloat(undefined) da' NaN, il confronto non decide niente e
 * l'ordinamento non faceva nulla. Il telefono ignorava la posizione e
 * metteva davanti la scheda predefinita. Risultato: riordinare le schede
 * dal backoffice non cambiava niente da nessuna delle due parti.
 *
 * `defaultTab` dice quale si APRE per prima, non dove sta nell'elenco: sono
 * due cose diverse e vanno tenute separate.
 *
 * Copia l'elenco invece di ordinarlo sul posto: l'originale arriva dalle
 * proprieta' del componente, e modificarlo e' il genere di cosa che poi fa
 * "non si aggiorna finche' non ricarico".
 */
export function ordinaSchede(schede) {
  return (schede || []).slice().sort(function (a, b) {
    const pa = Number(a && (a.position != null ? a.position : a.order));
    const pb = Number(b && (b.position != null ? b.position : b.order));
    if (Number.isNaN(pa) && Number.isNaN(pb)) return 0;
    if (Number.isNaN(pa)) return 1;
    if (Number.isNaN(pb)) return -1;
    return pa - pb;
  });
}

/** La linea di un mercato, letta dal suo id.
 *
 * Gli id sono `TIPO` oppure `TIPO@linea`: MATCH_RESULT, OVER_UNDER@2.5.
 * La linea sta dentro l'id, e questa e' la fonte da usare.
 *
 * NON usare il campo `sbv`: nei dati e' un ARRAY, e confrontarlo con === ha
 * gia' fatto danni — due array sono uguali solo se sono lo stesso oggetto,
 * quindi il confronto riusciva per una partita sola.
 */
export function lineaDelMercato(mercato) {
  if (!mercato || !mercato.id) return '';
  const parti = String(mercato.id).split('@');
  return parti.length > 1 ? parti[1] : '';
}

/**
 * DUE LINEE (sbv) SONO LA STESSA? Il campo arriva come array (["2.5"]), a
 * volte come testo ("2.5"), e per i mercati senza linea null, undefined o ''.
 * Con === due array uguali sono diversi appena passano da sessionStorage: la
 * quota Over 2.5 scelta non risultava piu' accesa dopo un cambio di pagina, e
 * toccata di nuovo si aggiungeva invece di togliersi. Qui si confronta il
 * valore: un array vale come i suoi elementi uniti, il vuoto come ''.
 */
export function stessaLinea(a, b) {
  const v = (x) => (x === null || x === undefined ? '' : Array.isArray(x) ? x.map(String).join(',') : String(x));
  return v(a) === v(b);
}

/** Il tipo di mercato, senza la linea. */
export function tipoDelMercato(mercato) {
  if (!mercato) return '';
  if (mercato.kind) return mercato.kind;
  return String(mercato.id || '').split('@')[0];
}

/** Le varianti di uno stesso mercato dentro una partita: stesso tipo, linee
 *  diverse. Ordinate per linea crescente, come numeri. */
export function varianti(partita, tipo) {
  return ((partita && partita.markets) || [])
    .filter(function (m) { return m && tipoDelMercato(m) === tipo; })
    .sort(function (a, b) {
      return parseFloat(lineaDelMercato(a)) - parseFloat(lineaDelMercato(b));
    });
}

/** Quale variante mostrare.
 *
 * In ordine:
 *   1. la linea che ha scelto chi guarda, se l'ha toccata;
 *   2. la linea configurata nella scheda, se quella partita ce l'ha;
 *   3. la PIU' VICINA a quella configurata fra quelle che ha.
 *
 * Il terzo punto conta piu' di quanto sembri: il fornitore non manda le
 * stesse linee per tutte le partite, quindi la scheda puo' chiedere 2.5 e
 * una partita avere solo 1.5 e 2.0. Prendere "quella di mezzo" sarebbe un
 * ripiego statistico; prendere la piu' vicina rispetta quello che ha
 * deciso chi ha configurato la scheda.
 *
 * Se la scheda non dichiara nessuna linea, allora si', quella di mezzo:
 * senza un riferimento e' la scelta meno arbitraria.
 */
export function varianteDaMostrare(elenco, lineaScelta, idDellaScheda,
    lineaPreferita) {
  if (!elenco || !elenco.length) return null;

  if (lineaScelta) {
    const scelta = elenco.filter(function (m) {
      return lineaDelMercato(m) === lineaScelta;
    });
    if (scelta.length) return scelta[0];
  }

  const configurata = elenco.filter(function (m) { return m.id === idDellaScheda; });
  if (configurata.length) return configurata[0];

  /* La piu' vicina a quella configurata, e A PARITA' quella SOTTO.
   *
   * Perche' non "sempre la piu' bassa": partendo da 0.5 si mostrerebbe
   * quasi ovunque un Over a 1.05, cioe' la quota meno interessante della
   * partita, come prima cosa che vede il cliente.
   *
   * Perche' a parita' quella sotto: fra 2.0 e 3.0 rispetto a 2.5 la
   * distanza e' la stessa, e la linea piu' bassa e' quella con l'evento
   * piu' probabile — la scelta prudente fra due pari. L'elenco arriva gia'
   * ordinato per numero crescente, quindi basta NON sostituire a parita'. */
  /* Il riferimento: prima la linea scritta nell'id del mercato della scheda
     (OVER_UNDER@2.5), poi la preferredLine dichiarata sul tab. Se la
     configurazione non dice niente, allora si' quella di mezzo — ma e' un
     ripiego, e si vede: basta valorizzare preferredLine per toglierlo. */
  let bersaglio = parseFloat(String(idDellaScheda || '').split('@')[1]);
  if (Number.isNaN(bersaglio)) bersaglio = parseFloat(lineaPreferita);
  if (!Number.isNaN(bersaglio)) {
    return elenco.reduce(function (migliore, m) {
      const d = Math.abs(parseFloat(lineaDelMercato(m)) - bersaglio);
      const dMigliore = Math.abs(parseFloat(lineaDelMercato(migliore)) - bersaglio);
      if (Number.isNaN(d)) return migliore;
      return d < dMigliore ? m : migliore;
    }, elenco[0]);
  }

  return elenco[Math.floor(elenco.length / 2)];
}

/** Tutte le linee di un tipo di mercato presenti nel campionato, crescenti.
 *
 * Serve al pannello del + : per far scegliere "fammi vedere tutti gli 1.5"
 * bisogna sapere quali linee esistono in GIRO, non quelle della singola
 * partita. L'unione, non l'intersezione: una linea che ce l'ha una partita
 * sola resta scegliibile, e le altre mostreranno il trattino.
 */
export function lineeDelCampionato(partite, tipo) {
  const viste = {};
  (partite || []).forEach(function (p) {
    varianti(p, tipo).forEach(function (m) {
      const l = lineaDelMercato(m);
      if (l !== '') viste[l] = true;
    });
  });
  return Object.keys(viste).sort(function (a, b) {
    return parseFloat(a) - parseFloat(b);
  });
}
